/**
 * E-GTCC-VIEW-TEN1, tab 1 — "Tenants".
 *
 * Reference: `GTCC_REFERENCE_2026-09-01.html:683-694` — the panel "Tenants"
 * with the `.mono muted` head addition "Suche: Name/ID · [+ Neuen Tenant
 * anlegen]", the six-column table and the `.sub` sentence underneath.
 *
 * The lifecycle buttons are NOT the reference's sample pairs. The reference
 * shows `[pause] [suspend]` for an active tenant and `[resume] [mark-trial]`
 * for a trial one; the server state machine
 * (`tenants/tenantLifecycle.ts:36`) offers `pause suspend cancel` for active
 * and `resume pause suspend cancel` for trial, and knows no `mark-trial` verb
 * at all. The Owner order settles it in the same panel's own sentence:
 * "Lebenszyklus-Verben folgen der Server-Zustandsmaschine" — so the row asks
 * the shared projection, and a terminal state draws the reference's em dash.
 *
 * Every verb posts to the lifecycle route gtcc1 already posts to, behind the
 * same confirmation dialog (`GtccTenantsView.tsx:1027`). No new write path.
 */
import { Button, ConfirmDialog, EmptyState, Stack, TextInput } from "@genius-tracker/design-system";
import { useLocale } from "@genius-tracker/i18n/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState, type CSSProperties, type ReactNode } from "react";

import { ApiError, UnauthenticatedError, apiSend } from "../../../../lib/api.js";
import {
  allowedTenantLifecycleVerbs,
  tenantLifecyclePath,
  type TenantLifecycleVerb,
} from "../../../../tenants/tenantLifecycle.js";
import { MEMBER_ROLE_TO_BUILTIN_ID } from "../../../../access/roles.js";
import { TenantLifecycleDatesDialog } from "../../../../tenants/TenantLifecycleDatesDialog.js";
import {
  TEN_TENANTS_KEY,
  fetchTenDevicesSummary,
  fetchTenInvitations,
  fetchTenLaunchStatus,
  fetchTenMembers,
  fetchTenTenant,
  fetchTenTenants,
  type TenLaunchStatus,
  type TenTenantDetail,
  type TenTenantRow,
} from "../../../api/ten.js";
import {
  FleetDot,
  FleetFreshBadge,
  GTCC_VISUALLY_HIDDEN,
  NotMeasurable,
  type FleetTone,
} from "../../cockpit/components/FleetPrimitives.js";
import {
  ACCENT_STYLE,
  GRID_COLUMN_STYLE,
  GRID_MAIN_STYLE,
  HEADING_NOTE_STYLE,
  HOST_STYLE,
  MONO_STYLE,
  SUB_STYLE,
  TABLE_CELL_STYLE,
  TABLE_HEAD_STYLE,
  TABLE_STYLE,
  TABLE_WRAP_STYLE,
  TEN_QUERY_OPTIONS,
  TenGate,
  TenMutedNote,
  TenPanel,
  TenRow,
  entitlementsValueText,
  launchValueText,
  lifecycleTokenText,
  lifecycleTone,
  memberValueText,
  type TenTabProps,
} from "../components/TenPanels.js";

/** Das Unendlich-Zeichen der Referenz steht fuer "kein Limit gesetzt". */
const UNLIMITED = "∞";

/*
 * E-GTCC-DPLANE-TEN-1ZU1 — `cancel` steht in der Referenz nur in der
 * 360°-Zeile "Identität + Lebenszyklus" (Zeile 699), nicht in der Liste
 * (Zeile 688-691). Das ist keine Schoenheitsfrage: `cancel` ist der einzige
 * Uebergang in einen Endzustand, aus dem die Zustandsmaschine
 * (`tenants/tenantLifecycle.ts:36`) nicht zurueckfuehrt. Er gehoert an die
 * Stelle, an der man einen Tenant vor sich hat, nicht in eine Zeile der
 * Liste. Die Zustandsmaschine bleibt unangetastet — gefiltert wird ihre
 * Anzeige an dieser einen Stelle.
 */
const LIST_HIDDEN_VERBS: ReadonlyArray<TenantLifecycleVerb> = ["cancel"];

const STATUS_CELL_STYLE: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "var(--gt-sys-space-2)",
};

const ACTION_CELL_STYLE: CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  gap: "var(--gt-sys-space-1)",
};

/*
 * Referenz Zeile 684 und 688-691: die Lebenszyklus-Verben und der Anlege-Weg
 * stehen als Mono-Klammertext (`[pause] [suspend]`,
 * `[+ Neuen Tenant anlegen]`), nicht als gefuellte oder umrandete Knoepfe.
 * Sie bleiben echte Knoepfe — die Mindest-Zielgroesse der Knopf-Primitive ist
 * eine Zugaenglichkeits-Entscheidung, kein Aussehen —, verlieren aber Rahmen
 * und Fuellung und nehmen die Mono-Schrift der Zelle (wie in `AlinView.tsx`).
 * Mit `size="sm"` bleibt die Zeile trotzdem hoeher als die 34 px der Referenz —
 * die Mindest-Zielgroesse ist der Rest, nicht entfernt.
 */
const BRACKET_BUTTON_STYLE: CSSProperties = {
  fontFamily: "var(--gt-sys-font-family-mono)",
  fontSize: "var(--gt-sys-font-size-xs)",
  fontWeight: "var(--gt-sys-font-weight-regular)",
  paddingInline: "var(--gt-sys-space-1)",
  gap: 0,
};

/** `[…]` of the reference — decoration, so it stays out of the button's name. */
function BracketLabel(props: { children: ReactNode }): ReactNode {
  return (
    <>
      <span aria-hidden="true">[</span>
      {props.children}
      <span aria-hidden="true">]</span>
    </>
  );
}

/*
 * Die Suche der Referenz ist eine schmale Zeile im Panelkopf, kein Formular
 * mit sichtbarer Beschriftung ueber einem Feld in voller Breite. Die
 * Beschriftung bleibt fuer Screenreader erhalten.
 *
 * E-GTCC-DPLANE-TEN-1ZU1 — die Referenz schreibt diese Zeile als Fliesstext
 * (`Suche: Name/ID · [+ Neuen Tenant anlegen]`, Zeile 684), nicht als
 * umrandetes Eingabefeld. Das Feld bleibt ein echtes Eingabefeld — die Suche
 * arbeitet —, verliert aber Rahmen, Fuellung und Flaeche und traegt
 * "Name/ID" als Platzhalter, genau wie die Referenz es schreibt. `outline`
 * wird ausdruecklich zurueckgeholt: ohne Rahmen ist der Fokusring der
 * einzige Hinweis, wo die Eingabe steht, und die Vorlage des Primitivs
 * schaltet ihn ab.
 */
const SEARCH_INPUT_STYLE: CSSProperties = {
  inlineSize: "7rem",
  paddingBlock: 0,
  paddingInline: 0,
  background: "none",
  borderColor: "transparent",
  borderRadius: 0,
  outline: "revert",
  fontFamily: "var(--gt-sys-font-family-mono)",
  fontSize: "var(--gt-sys-font-size-xs)",
  color: "var(--gt-sys-text-muted)",
};

function limitOf(value: number | null | undefined): string {
  return typeof value === "number" ? String(value) : UNLIMITED;
}

/** Wie `Tenant360Tab.tsx` — der Gesamtstatus der Launch-Checkliste. */
function launchTone(overall: string | undefined): FleetTone {
  if (overall === "ready") return "ok";
  if (overall === "blocked") return "crit";
  if (overall === "needs_action" || overall === "manual") return "warn";
  return "muted";
}

/** Wie `MembersTab.tsx` — ein aktives Mitglied ist gruen, gesperrt eine Warnung. */
function memberTone(status: string | null | undefined): FleetTone {
  if (status === "active") return "ok";
  if (status === "suspended" || status === "pending") return "warn";
  return "muted";
}

export function TenantsTab(props: TenTabProps): ReactNode {
  const { t, locale } = useLocale();
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [pending, setPending] = useState<{
    readonly tenant: TenTenantRow;
    readonly verb: TenantLifecycleVerb;
  } | null>(null);
  const [markTrialTenant, setMarkTrialTenant] = useState<TenTenantRow | null>(null);

  const query = useQuery({
    queryKey: TEN_TENANTS_KEY,
    queryFn: ({ signal }) => fetchTenTenants(signal),
    ...TEN_QUERY_OPTIONS,
  });

  const mutation = useMutation<unknown, Error, { tenantId: string; verb: TenantLifecycleVerb }>({
    mutationFn: ({ tenantId, verb }) =>
      apiSend<unknown>("POST", tenantLifecyclePath(tenantId, verb), {}),
    onSuccess: (_result, variables) => {
      setPending(null);
      void qc.invalidateQueries({ queryKey: TEN_TENANTS_KEY });
      void qc.invalidateQueries({
        queryKey: ["gtcc", "ten", "view", "tenant", variables.tenantId],
      });
      void qc.invalidateQueries({ queryKey: ["admin", "tenants"] });
    },
  });

  const errorMessage = useMemo(() => {
    const error = mutation.error;
    if (!error) return null;
    if (error instanceof UnauthenticatedError) {
      return t("admin.tenant_360.error.session_expired_body");
    }
    if (error instanceof ApiError) {
      return error.requestId
        ? t("admin.tenant_360.action.error.body_with_request", {
            status: error.status,
            requestId: error.requestId,
          })
        : t("admin.tenant_360.action.error.body", { status: error.status });
    }
    return error.message;
  }, [mutation.error, t]);

  const needle = search.trim().toLowerCase();

  /*
   * Die fuenf kompakten Uebersichts-Tafeln unter der Tabelle brauchen einen
   * Tenant, an dem sie messen — ohne eine Auswahl des Betrachters (noch kein
   * Sprung nach Tenant 360°) ist das der erste Treffer der Liste, genau wie
   * die Referenz eine feste Stichprobe zeigt. Kein neuer Endpunkt: dieselben
   * Abfragen wie in `Tenant360Tab.tsx`/`MembersTab.tsx`/`InvitationsTab.tsx`,
   * mit denselben literalen Schluesseln, teilen sich den Zwischenspeicher.
   */
  const tenantRows: readonly TenTenantRow[] =
    query.data !== undefined && query.data.ok ? query.data.data : [];
  const scopedTenantId = props.selectedTenantId ?? tenantRows[0]?.id ?? null;

  return (
    <Stack gap={3}>
      <TenPanel
        testId="gtcc-ten-tenants"
        title={t("admin.gtcc.ten.panel.tenants")}
        note={t("admin.gtcc.ten.panel.tenants_note")}
        headExtra={
          <>
            <TextInput
              label={t("admin.gtcc.ten.list.search_label")}
              visuallyHiddenLabel
              placeholder={t("admin.gtcc.ten.list.search_placeholder")}
              value={search}
              onChange={(event) => setSearch(event.currentTarget.value)}
              style={SEARCH_INPUT_STYLE}
              data-testid="gtcc-ten-search"
            />
            {/* Der `·`-Trenner der Referenz zwischen Suche und Anlege-Weg. */}
            <span style={HEADING_NOTE_STYLE} aria-hidden="true">
              ·
            </span>
            <Button
              variant="ghost"
              type="button"
              style={BRACKET_BUTTON_STYLE}
              onClick={() => props.onOpenTab("provisioning")}
              data-testid="gtcc-ten-new-tenant"
            >
              <BracketLabel>{t("admin.gtcc.ten.new_tenant")}</BracketLabel>
            </Button>
          </>
        }
      >
        <Stack gap={2}>
          <TenGate
            query={query}
            testId="gtcc-ten-tenants-gate"
            render={(rows) => {
              /*
               * Die Suche laeuft rein im Client ueber die Antwort, die schon da
               * ist — genau wie in der alten Konsole. Kein Query-Parameter,
               * keine zweite Anfrage, kein neuer Endpunkt.
               */
              const visible = rows.filter((row) => {
                if (needle.length === 0) return true;
                const name = (row.displayName ?? "").toLowerCase();
                return name.includes(needle) || row.id.toLowerCase().includes(needle);
              });
              if (visible.length === 0) {
                return (
                  <div data-testid="gtcc-ten-tenants-empty">
                    <EmptyState
                      title={
                        needle.length === 0
                          ? t("admin.gtcc.ten.list.empty")
                          : t("admin.gtcc.ten.list.no_matches")
                      }
                    />
                  </div>
                );
              }
              return (
                <div style={TABLE_WRAP_STYLE}>
                  <table style={TABLE_STYLE} data-testid="gtcc-ten-tenants-table">
                    {/*
                     * Nur fuer Screenreader: die Referenz zeichnet ueber der
                     * Tabelle keine zweite Zeile "Tenants" — die Ueberschrift
                     * des Panels steht schon darueber. Die Beschriftung bleibt
                     * im Zugaenglichkeitsbaum, weil jede GTCC-Tabelle eine
                     * `caption` fuehrt (gtcc-table-semantics.test.tsx).
                     */}
                    <caption style={GTCC_VISUALLY_HIDDEN}>
                      {t("admin.gtcc.ten.list.caption")}
                    </caption>
                    <thead>
                      <tr>
                        <th scope="col" style={TABLE_HEAD_STYLE}>
                          {t("admin.gtcc.ten.col.tenant")}
                        </th>
                        <th scope="col" style={TABLE_HEAD_STYLE}>
                          {t("admin.gtcc.ten.col.status")}
                        </th>
                        <th scope="col" style={TABLE_HEAD_STYLE}>
                          {t("admin.gtcc.ten.col.type")}
                        </th>
                        <th scope="col" style={TABLE_HEAD_STYLE}>
                          {t("admin.gtcc.ten.col.limits")}
                        </th>
                        <th scope="col" style={TABLE_HEAD_STYLE}>
                          {t("admin.gtcc.ten.col.actions")}
                        </th>
                        {/*
                         * E-GTCC-DPLANE-TEN-1ZU1 — die sechste Spalte der
                         * Referenz hat einen leeren Kopf (Zeile 686:
                         * `<th></th>`); die Zellen darunter tragen ihre
                         * eigene Beschriftung ("360° oeffnen →"). Sichtbar
                         * beschriftet stand hier eine Spaltenueberschrift, die
                         * es in der Referenz nicht gibt. Der Name bleibt im
                         * Zugaenglichkeitsbaum: eine Spalte ohne jeden Namen
                         * waere fuer Screenreader eine Spalte ohne Bedeutung.
                         */}
                        <th scope="col" style={TABLE_HEAD_STYLE}>
                          <span style={GTCC_VISUALLY_HIDDEN}>{t("admin.gtcc.ten.col.open")}</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {visible.map((row) => {
                        const lifecycleVerbs = allowedTenantLifecycleVerbs(
                          row.subscriptionStatus,
                        ).filter((verb) => !LIST_HIDDEN_VERBS.includes(verb));
                        const verbs =
                          row.displayName === "GELETEC"
                            ? []
                            : row.subscriptionStatus === "trial"
                              ? lifecycleVerbs.filter((verb) => verb === "resume")
                              : lifecycleVerbs;
                        const trialUntil =
                          row.subscriptionStatus === "trial" && row.trialUntil
                            ? t("admin.gtcc.ten.list.trial_until", {
                                date: new Intl.DateTimeFormat(locale, {
                                  day: "2-digit",
                                  month: "2-digit",
                                }).format(new Date(row.trialUntil)),
                              })
                            : null;
                        return (
                          <tr key={row.id} data-testid={`gtcc-ten-tenant-row-${row.id}`}>
                            {/*
                             * Zellen, keine Zeilenkoepfe: die GTCC-Tabellen
                             * fuehren ihre erste Spalte ueberall als `td`
                             * (gtcc-table-semantics.test.tsx misst je View
                             * null Zeilenkoepfe) — diese Tabelle bricht die
                             * Reihe nicht auf.
                             */}
                            <td style={{ ...TABLE_CELL_STYLE, ...HOST_STYLE }}>
                              {row.displayName ?? row.id}
                            </td>
                            <td style={TABLE_CELL_STYLE}>
                              <span style={STATUS_CELL_STYLE}>
                                <FleetDot tone={lifecycleTone(row.subscriptionStatus)} />
                                <span>
                                  {lifecycleTokenText(t, row.subscriptionStatus)}
                                  {trialUntil === null ? null : ` ${trialUntil}`}
                                </span>
                              </span>
                            </td>
                            <td style={{ ...TABLE_CELL_STYLE, ...MONO_STYLE }}>
                              {row.type ?? "—"}
                            </td>
                            <td style={{ ...TABLE_CELL_STYLE, ...MONO_STYLE }}>
                              {`${limitOf(row.maxDevices)} / ${limitOf(row.maxVehicles)} / ${limitOf(row.maxUsers)}`}
                            </td>
                            <td style={{ ...TABLE_CELL_STYLE, ...MONO_STYLE }}>
                              {verbs.length === 0 && row.subscriptionStatus !== "trial" ? (
                                <span data-testid={`gtcc-ten-tenant-actions-none-${row.id}`}>
                                  {"—"}
                                </span>
                              ) : (
                                <span style={ACTION_CELL_STYLE}>
                                  {verbs.map((verb) => (
                                    <Button
                                      key={verb}
                                      variant="ghost"
                                      size="sm"
                                      type="button"
                                      style={BRACKET_BUTTON_STYLE}
                                      disabled={mutation.isPending}
                                      onClick={() => {
                                        mutation.reset();
                                        setPending({ tenant: row, verb });
                                      }}
                                      data-testid={`gtcc-ten-tenant-action-${row.id}-${verb}`}
                                    >
                                      <BracketLabel>
                                        {t(`admin.gtcc.ten.list.verb.${verb}`)}
                                      </BracketLabel>
                                    </Button>
                                  ))}
                                  {row.subscriptionStatus === "trial" ? (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      type="button"
                                      style={BRACKET_BUTTON_STYLE}
                                      disabled={mutation.isPending}
                                      onClick={() => setMarkTrialTenant(row)}
                                      data-testid={`gtcc-ten-tenant-action-${row.id}-mark-trial`}
                                    >
                                      <BracketLabel>mark-trial</BracketLabel>
                                    </Button>
                                  ) : null}
                                </span>
                              )}
                            </td>
                            <td style={TABLE_CELL_STYLE}>
                              <button
                                type="button"
                                style={OPEN_BUTTON_STYLE}
                                onClick={() => props.onOpenTenant(row.id, "t360")}
                                data-testid={`gtcc-ten-open-360-${row.id}`}
                              >
                                {t("admin.gtcc.ten.open_360")}
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              );
            }}
          />

          <p style={SUB_STYLE} data-testid="gtcc-ten-tenants-sub">
            {t("admin.gtcc.ten.list.lifecycle_note")}
          </p>
        </Stack>
      </TenPanel>

      {scopedTenantId === null ? null : (
        <TenantOverview tenantId={scopedTenantId} onOpenTenant={props.onOpenTenant} />
      )}

      <ConfirmDialog
        open={pending !== null}
        title={t("admin.tenant_360.section.actions")}
        body={
          pending
            ? t(`admin.tenant_360.action.confirm.${pending.verb}`, {
                name: pending.tenant.displayName ?? pending.tenant.id,
              })
            : ""
        }
        confirmLabel={pending ? t(`admin.tenant_360.action.${pending.verb}`) : t("common.confirm")}
        cancelLabel={t("common.cancel")}
        tone={pending?.verb === "suspend" || pending?.verb === "cancel" ? "danger" : "default"}
        pending={mutation.isPending}
        errorMessage={errorMessage}
        onConfirm={() => {
          if (pending) mutation.mutate({ tenantId: pending.tenant.id, verb: pending.verb });
        }}
        onCancel={() => {
          if (!mutation.isPending) {
            mutation.reset();
            setPending(null);
          }
        }}
        testIdPrefix="gtcc-ten-tenants-confirm"
      />
      {markTrialTenant === null ? null : (
        <TenantLifecycleDatesDialog
          open
          onClose={() => setMarkTrialTenant(null)}
          tenantId={markTrialTenant.id}
          action="mark-trial"
          currentTrialUntil={markTrialTenant.trialUntil ?? null}
          /* The list response is intentionally a `TenTenantRow`; it does not
             carry the detail-only `validUntil` field. Do not manufacture a
             value from an incomplete list record. */
          currentValidUntil={null}
        />
      )}
    </Stack>
  );
}

/*
 * E-GTCC-VIEW-TEN1 — die fuenf Uebersichts-Tafeln unter der Tabelle brauchen
 * einen Tenant, an dem sie messen. Eigene Komponente statt bedingter Hooks in
 * `TenantsTab`: React verbietet, Hooks je nach Zustand auszulassen, und die
 * Fixtures dieser Views (`ten-test-fixture.ts`) fuehren bewusst keinen
 * Schluessel mit leerer Kennung — eine Lesestelle, die es nicht gibt, bekommt
 * auch keine Fixture. Nur gemountet, wenn `scopedTenantId` feststeht, damit
 * diese Abfragen nie mit einer leeren Kennung laufen.
 */
function TenantOverview(props: {
  readonly tenantId: string;
  readonly onOpenTenant: TenTabProps["onOpenTenant"];
}): ReactNode {
  const { t, locale } = useLocale();
  const tenantId = props.tenantId;

  const overviewDetail = useQuery({
    queryKey: ["gtcc", "ten", "view", "tenant", tenantId],
    queryFn: ({ signal }) => fetchTenTenant(tenantId, signal),
    ...TEN_QUERY_OPTIONS,
  });
  const overviewLaunch = useQuery({
    queryKey: ["gtcc", "ten", "view", "launch", tenantId],
    queryFn: ({ signal }) => fetchTenLaunchStatus(tenantId, signal),
    ...TEN_QUERY_OPTIONS,
  });
  const overviewDevices = useQuery({
    queryKey: ["gtcc", "ten", "view", "devices", tenantId],
    queryFn: ({ signal }) => fetchTenDevicesSummary(tenantId, signal),
    ...TEN_QUERY_OPTIONS,
  });
  const overviewMembers = useQuery({
    queryKey: ["gtcc", "ten", "view", "members", tenantId],
    queryFn: ({ signal }) => fetchTenMembers(tenantId, signal),
    ...TEN_QUERY_OPTIONS,
  });
  const overviewInvitations = useQuery({
    queryKey: ["gtcc", "ten", "view", "invitations", tenantId],
    queryFn: ({ signal }) => fetchTenInvitations(tenantId, signal),
    ...TEN_QUERY_OPTIONS,
  });

  const overviewTenant: TenTenantDetail | null =
    overviewDetail.data !== undefined && overviewDetail.data.ok ? overviewDetail.data.data : null;
  const overviewLaunchData: TenLaunchStatus | null =
    overviewLaunch.data !== undefined && overviewLaunch.data.ok ? overviewLaunch.data.data : null;
  const overviewDeviceTotal =
    overviewDevices.data !== undefined && overviewDevices.data.ok
      ? overviewDevices.data.data.total
      : null;
  const overviewMemberCount =
    overviewMembers.data !== undefined && overviewMembers.data.ok
      ? overviewMembers.data.data.length
      : null;
  const overviewOpenInvitations =
    overviewInvitations.data !== undefined && overviewInvitations.data.ok
      ? overviewInvitations.data.data.filter((row) => row.status === "pending").length
      : null;

  const unmeasured = <NotMeasurable />;
  const overviewValidUntil =
    overviewTenant?.validUntil === undefined || overviewTenant.validUntil === null
      ? null
      : new Intl.DateTimeFormat(locale, { dateStyle: "short" }).format(
          new Date(overviewTenant.validUntil),
        );
  const overviewEntitlements =
    overviewTenant?.resolvedEntitlements ?? overviewTenant?.entitlements ?? null;

  /* Wie `RolesTab.tsx` — gemessen, nicht behauptet. */
  const decorativeTags = Object.entries(MEMBER_ROLE_TO_BUILTIN_ID)
    .filter(([, builtIn]) => builtIn === null)
    .map(([tag]) => tag);

  return (
    <div style={GRID_MAIN_STYLE} data-testid="gtcc-ten-overview">
      <div style={GRID_COLUMN_STYLE}>
        <TenPanel
          testId="gtcc-ten-overview-360"
          title={t("admin.gtcc.ten.t360.panel", {
            name: overviewTenant?.displayName ?? tenantId,
          })}
          note={
            overviewLaunchData?.computedAt ? (
              <FleetFreshBadge
                seconds={undefined}
                measuredAt={overviewLaunchData.computedAt}
                testId="gtcc-ten-overview-360-fresh"
              />
            ) : (
              <NotMeasurable testId="gtcc-ten-overview-360-fresh" />
            )
          }
        >
          <Stack gap={0}>
            <TenRow
              testId="gtcc-ten-overview-360-launch"
              tone={launchTone(overviewLaunchData?.overall)}
              label={t("admin.gtcc.ten.t360.row.launch")}
              value={launchValueText(t, overviewLaunchData) ?? unmeasured}
            />
            <TenRow
              testId="gtcc-ten-overview-360-identity"
              tone={lifecycleTone(overviewTenant?.subscriptionStatus)}
              label={t("admin.gtcc.ten.t360.row.identity")}
              value={
                overviewTenant === null
                  ? unmeasured
                  : `${lifecycleTokenText(t, overviewTenant.subscriptionStatus)} · ${
                      overviewTenant.type ?? "—"
                    }`
              }
            />
            <TenRow
              testId="gtcc-ten-overview-360-subscription"
              tone={lifecycleTone(overviewTenant?.subscriptionStatus)}
              label={t("admin.gtcc.ten.t360.row.subscription")}
              value={
                overviewTenant === null
                  ? unmeasured
                  : overviewValidUntil === null
                    ? t("admin.gtcc.ten.t360.valid_until_none")
                    : t("admin.gtcc.ten.t360.valid_until", { date: overviewValidUntil })
              }
            />
            <TenRow
              testId="gtcc-ten-overview-360-entitlements"
              tone="info"
              label={t("admin.gtcc.ten.t360.row.entitlements")}
              value={entitlementsValueText(t, overviewEntitlements) ?? unmeasured}
            />
            <TenRow
              testId="gtcc-ten-overview-360-ai"
              tone="info"
              label={t("admin.gtcc.ten.t360.row.ai")}
              value={unmeasured}
            />
            <TenRow
              testId="gtcc-ten-overview-360-quotas"
              tone="muted"
              label={t("admin.gtcc.ten.t360.row.quotas")}
              value={
                overviewTenant === null
                  ? unmeasured
                  : `${limitOf(overviewTenant.maxDevices)} / ${limitOf(overviewTenant.maxVehicles)} / ${limitOf(overviewTenant.maxUsers)}`
              }
            />
            <TenRow
              testId="gtcc-ten-overview-360-devices"
              tone="muted"
              label={t("admin.gtcc.ten.t360.row.devices")}
              value={overviewDeviceTotal === null ? unmeasured : String(overviewDeviceTotal)}
            />
            <TenRow
              testId="gtcc-ten-overview-360-appearance"
              tone="muted"
              label={t("admin.gtcc.ten.t360.row.appearance")}
              value={unmeasured}
            />
            <TenRow
              testId="gtcc-ten-overview-360-members"
              tone="muted"
              label={t("admin.gtcc.ten.t360.row.members")}
              value={overviewMemberCount === null ? unmeasured : String(overviewMemberCount)}
            />
            <TenRow
              testId="gtcc-ten-overview-360-onboard"
              tone="info"
              label={t("admin.gtcc.ten.t360.row.onboard")}
              value={
                overviewOpenInvitations === null ? unmeasured : String(overviewOpenInvitations)
              }
            />
            <TenRow
              testId="gtcc-ten-overview-360-domain"
              tone="info"
              label={t("admin.gtcc.ten.t360.row.domain")}
              value={overviewTenant?.slug ?? unmeasured}
            />
          </Stack>
        </TenPanel>

        <TenPanel
          testId="gtcc-ten-overview-provisioning"
          title={t("admin.gtcc.ten.provisioning.panel")}
        >
          <Stack gap={0}>
            <TenRow
              testId="gtcc-ten-overview-provisioning-step1"
              tone="info"
              label={t("admin.gtcc.ten.provisioning.step1")}
              value={t("admin.gtcc.ten.provisioning.step1_value")}
            />
            <TenRow
              testId="gtcc-ten-overview-provisioning-step2"
              tone="muted"
              label={t("admin.gtcc.ten.provisioning.step2")}
              value={t("admin.gtcc.ten.provisioning.step2_value")}
            />
            <TenRow
              testId="gtcc-ten-overview-provisioning-step3"
              tone="info"
              label={t("admin.gtcc.ten.provisioning.step3")}
              value={t("admin.gtcc.ten.provisioning.step3_value")}
            />
            <TenRow
              testId="gtcc-ten-overview-provisioning-step4"
              tone="muted"
              label={t("admin.gtcc.ten.provisioning.step4")}
              value={t("admin.gtcc.ten.provisioning.step4_value")}
            />
          </Stack>
        </TenPanel>
      </div>

      <div style={GRID_COLUMN_STYLE}>
        <TenPanel
          testId="gtcc-ten-overview-members"
          title={t("admin.gtcc.ten.members_tab.panel", {
            name: overviewTenant?.displayName ?? tenantId,
          })}
          /*
           * E-GTCC-DPLANE-TEN-1ZU1 — die Referenz traegt im Kopf dieser Tafel
           * `[+ Einladen] [+ Direkt anlegen]` (Zeile 726), keinen
           * "oeffnen →"-Verweis. Beide Wege fuehren in den Tab "Mitglieder":
           * dort steht die Schreibflaeche der alten Konsole, und dort ist
           * "direkt anlegen" derselbe Pfad wie "einladen" — gtcc1 legt ein
           * Mitglied an, indem es einlaedt (`MembersTab.tsx:13`). Zwei
           * Beschriftungen fuer denselben Weg, weil die Referenz zwei
           * schreibt; kein zweiter Schreibpfad und keine erfundene Maske.
           */
          headExtra={
            <>
              <Button
                variant="ghost"
                size="sm"
                type="button"
                style={BRACKET_BUTTON_STYLE}
                onClick={() => props.onOpenTenant(tenantId, "members")}
                data-testid="gtcc-ten-overview-members-invite"
              >
                <BracketLabel>{t("admin.gtcc.ten.members_tab.invite")}</BracketLabel>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                type="button"
                style={BRACKET_BUTTON_STYLE}
                onClick={() => props.onOpenTenant(tenantId, "members")}
                data-testid="gtcc-ten-overview-members-create"
              >
                <BracketLabel>{t("admin.gtcc.ten.members_tab.create_direct")}</BracketLabel>
              </Button>
            </>
          }
        >
          <TenGate
            query={overviewMembers}
            testId="gtcc-ten-overview-members-gate"
            render={(rows) =>
              rows.length === 0 ? (
                <div data-testid="gtcc-ten-overview-members-empty">
                  <EmptyState title={t("admin.gtcc.ten.members.empty")} />
                </div>
              ) : (
                <Stack gap={0}>
                  {rows.map((row) => (
                    <TenRow
                      key={row.id}
                      testId={`gtcc-ten-overview-member-${row.id}`}
                      tone={memberTone(row.status)}
                      label={row.displayName ?? row.email ?? row.id}
                      value={memberValueText(t, row.role, row.status)}
                    />
                  ))}
                </Stack>
              )
            }
          />
        </TenPanel>

        <TenPanel
          testId="gtcc-ten-overview-invitations"
          title={t("admin.gtcc.ten.members_tab.invitations_panel")}
        >
          <TenGate
            query={overviewInvitations}
            testId="gtcc-ten-overview-invitations-gate"
            render={(rows) =>
              rows.filter((row) => row.status === "pending").length === 0 ? (
                /*
                 * E-GTCC-DPLANE-TEN-1ZU1 — die Referenz schreibt auch den
                 * leeren Fall als Zeile: Punkt, "Keine offen" und daneben der
                 * Mono-Wert mit dem Weg zu einer neuen Einladung (Zeile 723).
                 * Vorher stand hier ein ausgegrauter Satz ohne Punkt und ohne
                 * Weg — dieselbe Aussage, aber nicht die Form der Referenz,
                 * und der Betrachter sah nicht, wie eine Einladung entsteht.
                 */
                <TenRow
                  testId="gtcc-ten-overview-invitations-none"
                  tone="ok"
                  label={t("admin.gtcc.ten.members_tab.invitations_none")}
                  value={t("admin.gtcc.ten.members_tab.invitations_none_value")}
                />
              ) : (
                <Stack gap={0}>
                  {rows
                    .filter((row) => row.status === "pending")
                    .map((row) => (
                      <TenRow
                        key={row.id}
                        testId={`gtcc-ten-overview-invitation-${row.id}`}
                        tone="warn"
                        label={row.email ?? row.id}
                        value={row.role ?? "—"}
                      />
                    ))}
                </Stack>
              )
            }
          />
        </TenPanel>

        <TenPanel testId="gtcc-ten-overview-roles" title={t("admin.gtcc.ten.roles.panel")}>
          <Stack gap={2}>
            <TenRow
              testId="gtcc-ten-overview-roles-list"
              tone="muted"
              label={t("admin.gtcc.ten.roles.row_label")}
              value={t("admin.gtcc.ten.roles.row_value")}
            />
            <TenMutedNote testId="gtcc-ten-overview-roles-note">
              {decorativeTags.length === 0
                ? t("admin.gtcc.ten.roles.note_effective")
                : t("admin.gtcc.ten.roles.note_decorative", {
                    tags: decorativeTags.join(", "),
                  })}
            </TenMutedNote>
          </Stack>
        </TenPanel>
      </div>
    </div>
  );
}

const OPEN_BUTTON_STYLE: CSSProperties = {
  ...ACCENT_STYLE,
  appearance: "none",
  background: "none",
  border: "none",
  padding: 0,
  cursor: "pointer",
  textAlign: "start",
};
